import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CodeXml } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';

const AdminLogin = () => {
  const [username] = useState('admin'); // Remove setter, make it constant
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  const { login, error, isAuthenticated, loading, clearError } = useAuth();

  // Redirect if already authenticated (only check once after loading is done)
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/blog/admin/dashboard');
    }
  }, [loading, isAuthenticated, navigate]);

  // Clear error when password changes
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [password, clearError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      return;
    }

    setIsLoading(true);
    
    try {
      const result = await login(username, password);
      
      if (result.success) {
        // Don't navigate here - let the useEffect handle it
        // This prevents race conditions
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <Helmet>
        <title>Admin Login | TukeWIN Blog</title>
        <meta name="description" content="Admin login for TukeWIN Blog" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="login-container">
        <form onSubmit={handleSubmit} className="login-form">
          <div className="text-center mb-8">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3 cursor-pointer">
                <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <CodeXml className="w-4 h-4 text-white" strokeWidth={2} aria-hidden="true" />
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                  TukeWIN
                </span>
              </div>
            </div>
          </div>

          {error && (
            <div className="form-error mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-center">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* Hidden username input with fixed value */}
            <input
              type="hidden"
              name="username"
              value={username}
              readOnly
            />

            <div className="form-group">
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input pl-10 pr-10"
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !password.trim()}
              className="admin-btn w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="spinner w-5 h-5 mr-2"></div>
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AdminLogin;
